import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle
} from 'react'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import { SkeletonUtils } from 'three-stdlib'
import {cloneAndScaleUV, setupTextureRepeat} from "../../utils/geometryUltis"
import useGateBoundingDataChecker from "../../utils/gateBoundingDataChecker"

const BramaUchylna = forwardRef(({ gate, onUpdate, textures, ...props }, ref) => {
  const group = useRef()
  const { scene, animations } = useGLTF('/models/gates/Brama-uchylna-v4.glb')
  const cloned = useRef()

  // Clone and UV scale once per change
  useEffect(() => {
    if (!scene) return

    const newClone = SkeletonUtils.clone(scene)

    // Scale UVs only once during the clone
    const bone = newClone.getObjectByName('Bone003')
    if (bone) {
      bone.traverse((child) => {
        if (child.isMesh && child.geometry) {
          const clonedGeometry = child.geometry.clone()
          const scaledGeometry = cloneAndScaleUV(
            clonedGeometry,
            1,
            1,
            1,
            gate.width / 100,
            gate.height / 100
          )
          child.geometry = scaledGeometry
          child.material = child.material.clone()

          if (!textures || !textures.gates_swing_color) return;

          const gatesColor = textures.gates_swing_color;

          const material = child.material.clone();

          material.map = gatesColor.textureDiff;
          material.metalnessMap = gatesColor.textureMetalness;
          material.normalMap = gatesColor.textureNormalness;

          setupTextureRepeat(material.map, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical);
          setupTextureRepeat(material.metalnessMap, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical);
          setupTextureRepeat(material.normalMap, 1 / gatesColor.multiplierHorizontal, 1 / gatesColor.multiplierVertical);

          material.metalness = 1;
          material.roughness = 0.5;
          material.color = new THREE.Color(gatesColor.hex);
          material.needsUpdate = true;

          if (child.material.name === 'Material') child.material = material;
        }
      })
    }

    cloned.current = newClone
    group.current.clear()
    group.current.add(newClone)
  }, [scene, gate.width, props.length, props.width, textures])

  // Extract nodes and materials
  const { nodes, materials } = useMemo(() => {
    const nodes = {}
    const materials = {}
    if (cloned.current) {
      cloned.current.traverse((obj) => {
        if (obj.name) nodes[obj.name] = obj
        if (obj.material) materials[obj.material.name] = obj.material
      })
    }
    return { nodes, materials }
  }, [cloned.current])

  const [scaledAnimations, setScaledAnimations] = useState([])
  const [isOpen, setIsOpen] = useState(false)
  const isOpenRef = useRef(false)
  const isAnimatingRef = useRef(false)
  const mixerRef = useRef(null)

  useImperativeHandle(ref, () => group.current)

  // Clone animations
  useEffect(() => {
    if (!group.current) return

    const newScaledAnimations = animations.map((clip) => {
      const clonedTracks = clip.tracks.map((track) => {
        if (track.name.endsWith(".position")) {
          const times = Array.from(track.times)
          const values = Array.from(track.values)
          for (let i = 0; i < values.length; i += 3) {
            values[i] = values[i]
            values[i + 1] = values[i + 1]
            values[i + 2] = values[i + 2]
          }
          return new THREE.VectorKeyframeTrack(track.name, times, values)
        }
        return track.clone()
      })
      return new THREE.AnimationClip(clip.name, -1, clonedTracks)
    })

    setScaledAnimations(newScaledAnimations)
  }, [animations, gate.width, gate.height, gate.position, props.width, props.length, textures])

  // Mixer setup
  useEffect(() => {
    if (!group.current) return

    if (mixerRef.current) {
      mixerRef.current.stopAllAction()
      mixerRef.current.uncacheRoot(group.current)
    }

    mixerRef.current = new THREE.AnimationMixer(group.current)

    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.clampWhenFinished = true
      action.enabled = true
      action.paused = true
    })
  }, [scaledAnimations])

  // Animation loop
  useEffect(() => {
    let frameId
    const animate = () => {
      mixerRef.current?.update(1 / 60)
      frameId = requestAnimationFrame(animate)
    }
    frameId = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(frameId)
  }, [])

  // Reset animation when position changes
  useEffect(() => {
    scaledAnimations.forEach(clip => {
      const action = mixerRef.current.clipAction(clip)
      action.reset()
      action.stop()
    })

    setIsOpen(false)
    isOpenRef.current = false
    isAnimatingRef.current = false
  }, [gate.position])

  // Bounding box updater
  useGateBoundingDataChecker({
    nodes,
    gate,
    onBoundingDataChanged: (newBoundingData) => {
      if (isAnimatingRef.current) return

      const box = new THREE.Box3().setFromObject(group.current)
      const size = new THREE.Vector3()
      box.getSize(size)

      const updateData = {
        boundingData: { box, size }
      }

      if (!isOpen) {
        const [x = 0, y = 0, z = 0] = [(-props.length / 100) / 2 + 0.5, 0, gate.position.z]
        const adjustedY = y + gate.height / 100 / 2

        updateData.closedBoundingBox = { box: box.clone(), size: size.clone() }
        updateData.positionAdjusted = [x, adjustedY, z]
        onUpdate(updateData)
      }
    },
    group,
  })

  // Click to open/close
  const handleClick = () => {
    if (!mixerRef.current || scaledAnimations.length === 0) return

    const clip = scaledAnimations[0]
    const action = mixerRef.current.clipAction(clip)
    action.reset()
    action.setLoop(THREE.LoopOnce, 1)
    action.clampWhenFinished = true
    action.enabled = true
    action.paused = false

    isAnimatingRef.current = true

    const onFinished = () => {
      isAnimatingRef.current = false
      mixerRef.current.removeEventListener('finished', onFinished)
    }

    mixerRef.current.addEventListener('finished', onFinished)

    if (!isOpen) {
      action.timeScale = 1
      action.play()
    } else {
      action.time = clip.duration
      action.timeScale = -1
      action.play()
    }

    setIsOpen(!isOpen)
  }

  return (
    <group
      ref={group}
      wall={'front_wall'}
      dispose={null}
      position={[gate.position.x, gate.position.y, gate.position.z]}
      castShadow={true}
      scale={[gate.height / 100, gate.height / 100, gate.width / 100]}
      onClick={handleClick}
    />
  )
})

export default BramaUchylna

useGLTF.preload('/models/gates/Brama-uchylna-v4.glb')
